/**
 * AIWARI top-level app shell — manages stage-routing between splash,
 * character creation flow, and the world. Babylon engine is mounted once
 * for the whole session and stays in the background even on HTML stages
 * (so transitions are instant once we get to the world).
 */

import { useEffect } from 'react';
import { AuthScreen } from './components/AuthScreen.js';
import { CharacterCreator } from './components/CharacterCreator.js';
import { CharacterRoster } from './components/CharacterRoster.js';
import { NpcInteractPrompt } from './components/NpcInteractPrompt.js';
import { PermadeathScroll } from './components/PermadeathScroll.js';
import { PwaInstallPrompt } from './components/PwaInstallPrompt.js';
import { RealmSelect } from './components/RealmSelect.js';
import { FlightDialog } from './components/FlightDialog.js';
import { QuestAcceptModal } from './components/QuestAcceptModal.js';
import { QuestTurnInModal } from './components/QuestTurnInModal.js';
import { RepairDialog } from './components/RepairDialog.js';
import { ServerNpcDialog } from './components/ServerNpcDialog.js';
import { ServerQuestLog } from './components/ServerQuestLog.js';
import { GMPanel } from './components/GMPanel.js';
import { World } from './components/World.js';
import { BabylonEngine } from './scene/Engine.js';
import { useAuth } from './game/auth.js';
import { useNet } from './state/net-store.js';
import { useQuestPoi } from './state/quest-poi-store.js';
import { useUI } from './state/store.js';
import { vanillaData } from './data/vanilla.js';
import { getAdaptedVanillaQuests } from './data/vanilla-quest-adapter.js';

export function App() {
  const stage = useUI((s) => s.stage);
  const setStage = useUI((s) => s.setStage);
  const userId = useAuth((s) => s.userId);
  const authLoading = useAuth((s) => s.loading);
  const worldOrCreator = stage === 'world' || stage === 'character-creator';

  // R128: kick off WotLK 3.3.5a data loading at boot so NpcDialog opens instantly later.
  useEffect(() => {
    void vanillaData.quests();
    void vanillaData.creatures();
    void getAdaptedVanillaQuests();
  }, []);

  // R232q84: orientation/viewport class on <body> so CSS can react to
  // portrait vs landscape without media-query duplication. Pairs with the
  // rubber/elastic vmin tokens in sirus-tokens.css — components opt into
  // `body.is-portrait .my-component { ... }` selectors when they need a
  // different stacking on phones held vertically.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(orientation: portrait)');
    const apply = () => {
      document.body.classList.toggle('is-portrait', mq.matches);
      document.body.classList.toggle('is-landscape', !mq.matches);
    };
    apply();
    // Some browsers fire `change` on the MediaQueryList; older ones only on
    // window resize / orientationchange. Listen to all three for safety.
    const onChange = () => apply();
    mq.addEventListener?.('change', onChange);
    window.addEventListener('resize', onChange);
    window.addEventListener('orientationchange', onChange);
    return () => {
      mq.removeEventListener?.('change', onChange);
      window.removeEventListener('resize', onChange);
      window.removeEventListener('orientationchange', onChange);
    };
  }, []);

  // R232q22 Phase 3: пересчёт Quest POI («!»/«?» на minimap + worldmap) при
  // смене уровня игрока или при принятии / сдаче квеста.
  // accepted / completed читаем из localStorage (legacy ключи NpcQuestMarkers
  // — `aiwari-quest-state:accepted` / `aiwari-quest-state:completed`).
  useEffect(() => {
    const STORAGE_ACC = 'aiwari-quest-state:accepted';
    const STORAGE_COM = 'aiwari-quest-state:completed';

    const recompute = () => {
      const net = useNet.getState();
      const me = net.selfId ? net.players[net.selfId] : null;
      const selfLevel = me?.level ?? 1;
      let acceptedSet = new Set<string>();
      let completedSet = new Set<string>();
      try {
        const acc = JSON.parse(localStorage.getItem(STORAGE_ACC) ?? '[]') as Array<{ questId?: string }>;
        if (Array.isArray(acc)) {
          acceptedSet = new Set(acc.map((a) => a?.questId).filter((x): x is string => typeof x === 'string'));
        }
      } catch { /* ignore — пустой Set безопасен. */ }
      try {
        const com = JSON.parse(localStorage.getItem(STORAGE_COM) ?? '[]') as string[];
        if (Array.isArray(com)) {
          completedSet = new Set(com.filter((x): x is string => typeof x === 'string'));
        }
      } catch { /* ignore */ }
      useQuestPoi.getState().recompute({
        selfLevel,
        accepted: acceptedSet,
        completed: completedSet,
      });
    };

    // Первый расчёт + подписка на изменения уровня игрока.
    recompute();
    const unsub = useNet.subscribe((state, prev) => {
      const id = state.selfId;
      if (!id) return;
      const cur = state.players[id];
      const prv = prev.selfId ? prev.players[prev.selfId] : null;
      if (!cur) return;
      if (!prv || cur.level !== prv.level) recompute();
    });

    // Window-события: принять/сдать квест + прогресс по objective.
    const onAccept = () => recompute();
    const onComplete = () => recompute();
    const onProgress = () => recompute();
    const onTurnin = () => recompute();
    window.addEventListener('aiwari-quest-accept', onAccept as EventListener);
    window.addEventListener('aiwari-accept-quest', onAccept as EventListener);
    window.addEventListener('aiwari-quest-complete', onComplete as EventListener);
    window.addEventListener('aiwari-complete-quest', onComplete as EventListener);
    window.addEventListener('aiwari-quest-turnin', onTurnin as EventListener);
    window.addEventListener('aiwari-quest-progress', onProgress as EventListener);
    window.addEventListener('storage', recompute);

    return () => {
      unsub();
      window.removeEventListener('aiwari-quest-accept', onAccept as EventListener);
      window.removeEventListener('aiwari-accept-quest', onAccept as EventListener);
      window.removeEventListener('aiwari-quest-complete', onComplete as EventListener);
      window.removeEventListener('aiwari-complete-quest', onComplete as EventListener);
      window.removeEventListener('aiwari-quest-turnin', onTurnin as EventListener);
      window.removeEventListener('aiwari-quest-progress', onProgress as EventListener);
      window.removeEventListener('storage', recompute);
    };
  }, []);

  // R232q71: stage routing guard.
  //  - legacy 'splash' → 'auth' (or 'realm-select' if уже залогинен).
  //  - any pre-world stage with no userId → 'auth'.
  //  - 'auth' с уже подгруженной session → 'realm-select'.
  useEffect(() => {
    if (authLoading) return;
    if (stage === 'splash') {
      setStage(userId ? 'realm-select' : 'auth');
      return;
    }
    if (!userId && stage !== 'auth') {
      setStage('auth');
      return;
    }
    if (userId && stage === 'auth') {
      setStage('realm-select');
    }
  }, [stage, userId, authLoading, setStage]);

  return (
    <BabylonEngine active={worldOrCreator}>
      {stage === 'splash' && <AuthScreen />}
      {stage === 'auth' && <AuthScreen />}
      {stage === 'realm-select' && <RealmSelect />}
      {/* R232q72: PWA install pill — visible only on pre-world stages so it
          never overlaps gameplay HUD. Hides itself if already installed or
          if the user already dismissed it. */}
      {(stage === 'splash' || stage === 'auth' || stage === 'realm-select') && <PwaInstallPrompt />}
      {stage === 'character-roster' && <CharacterRoster />}
      {stage === 'character-creator' && <CharacterCreator />}
      {stage === 'world' && <World />}
      {stage === 'world' && <NpcInteractPrompt />}
      {stage === 'world' && <ServerQuestLog />}
      {stage === 'world' && <GMPanel />}
      {/* R232q5r-hardcore: глобальный wax-seal overlay для permadeath events. */}
      <PermadeathScroll />
      {/* R232q5s: server NpcRegistry dialogue panel — listens to
          'aiwari-server-npc-open' window event with detail.npcId. */}
      <ServerNpcDialog />
      {/* R232q28: equipment repair modal — listens to `aiwari-open-repair`
          dispatched by ServerNpcDialog when a repair NPC offers the action. */}
      <RepairDialog />
      {/* R232q5ad: parchment-scroll quest accept / turn-in modals.
          QuestAcceptModal listens to `aiwari-quest-offer`; QuestTurnInModal
          listens to `aiwari-quest-complete`. Both are dispatched by
          ServerNpcDialog (sideEffect bridge) + ServerQuestLog (turn-in). */}
      <QuestAcceptModal />
      <QuestTurnInModal />
      {/* R232q5s Tier 3: flight master destination picker. */}
      <FlightDialog />
    </BabylonEngine>
  );
}
